import React, { useState } from "react";
import { Box, Typography, Avatar, Menu, MenuItem, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import { FiUsers, FiChevronDown, FiCheck } from "react-icons/fi";
import { T, STATUS_LABEL, STATUS_COLOR } from "../../pages/TodoTask/constants/todoTokens";
import { initials, hueFor, timeAgo, completionSummary, WORK_STATUSES } from "./utils";
import { TASK_STATUS } from "../../constant/constant";

const CARD_W = 300;

const StatusChip = ({ status, editable, busy, onPick, onMenuOpenChange, personName }) => {
  const [anchor, setAnchor] = useState(null);
  const c = STATUS_COLOR[status] || STATUS_COLOR[1];
  const open = Boolean(anchor);

  const setOpen = (el) => {
    setAnchor(el);
    onMenuOpenChange?.(Boolean(el));
  };

  return (
    <>
      <Box
        component={editable ? "button" : "div"}
        type={editable ? "button" : undefined}
        disabled={editable ? busy : undefined}
        aria-haspopup={editable ? "menu" : undefined}
        aria-expanded={editable ? open : undefined}
        aria-label={
          editable
            ? `${personName}: ${STATUS_LABEL[status]} — change status`
            : `${personName}: ${STATUS_LABEL[status]}`
        }
        onClick={editable ? (e) => { e.stopPropagation(); setOpen(e.currentTarget); } : undefined}
        sx={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          px: "7px",
          py: "2px",
          borderRadius: "6px",
          bgcolor: c.bg,
          color: c.color,
          border: `1px solid ${c.color}22`,
          font: "inherit",
          fontSize: "10.5px",
          fontWeight: 700,
          lineHeight: 1.6,
          whiteSpace: "nowrap",
          cursor: editable ? "pointer" : "default",
          transition: "box-shadow .15s, border-color .15s, transform .12s",
          ...(editable && {
            "&:hover": { borderColor: `${c.color}66`, boxShadow: `0 0 0 3px ${c.color}14` },
            "&:active": { transform: "scale(0.97)" },
            "&:focus-visible": { outline: "none", boxShadow: `0 0 0 3px ${T.primaryGlow}`, borderColor: T.primary },
            "&:disabled": { opacity: 0.55, cursor: "wait" },
          }),
        }}
      >
        {busy ? <CircularProgress size={9} sx={{ color: c.color, mr: "2px" }} /> : null}
        {STATUS_LABEL[status] || "—"}
        {editable && <FiChevronDown size={10} style={{ marginRight: -2, opacity: 0.7 }} />}
      </Box>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setOpen(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5, minWidth: 150, borderRadius: "10px",
              border: `1px solid ${T.border}`, boxShadow: T.shadow.lg, p: "4px",
            },
          },
        }}
      >
        {WORK_STATUSES.map((s) => {
          const sc = STATUS_COLOR[s];
          const active = s === status;
          return (
            <MenuItem
              key={s}
              selected={active}
              onClick={() => { setOpen(null); if (!active) onPick(s); }}
              sx={{
                borderRadius: "7px", px: 1, py: 0.6, gap: 1,
                fontSize: "12.5px", fontWeight: 600, color: T.text,
                "&.Mui-selected": { bgcolor: T.primaryLight },
                "&.Mui-selected:hover": { bgcolor: T.primaryLight },
                "&:hover": { bgcolor: T.borderLight },
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: sc.color, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>{STATUS_LABEL[s]}</Box>
              {active && <FiCheck size={13} color={T.primary} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

/**
 * The per-assignee completion card.
 *
 * variant="popover" — floating hover card (default)
 * variant="inline"  — same body without the elevated shell; used on the task
 *                     detail page, where this should not hide behind a hover.
 *
 * Deliberately a LIGHT surface, unlike TaskPill's dark tooltip: STATUS_COLOR's
 * backgrounds are 95%-lightness tints that turn into near-white blocks on a
 * dark card (which is why TaskPill had to hand-roll its own overlays), and this
 * card is interactive — focusable controls belong on an elevated light surface.
 */
const AssigneeHoverCard = ({
  assignees,
  canEdit,
  pendingId,
  onPickStatus,
  onMenuOpenChange,
  variant = "popover",
  title = "Assignees",
  solo = false,
}) => {
  const { done, total, pct } = completionSummary(assignees);

  const shell =
    variant === "popover"
      ? {
          width: CARD_W, bgcolor: T.surface, borderRadius: "12px",
          border: `1px solid ${T.border}`, boxShadow: T.shadow.lg, overflow: "hidden",
        }
      : {
          width: "100%", bgcolor: T.surface, borderRadius: "10px",
          border: `1px solid ${T.borderLight}`, overflow: "hidden",
        };

  if (!total) {
    return (
      <Box sx={{ ...shell, p: "14px 14px 12px" }} role="group" aria-label="No assignees">
        <Typography sx={{ fontSize: "12px", color: T.textMuted, fontStyle: "italic", textAlign: "center" }}>
          No one is assigned to this task yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={shell}>
      {/* Header */}
      <Box sx={{ px: "14px", pt: "11px", pb: "9px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: solo ? 0 : "8px" }}>
          <FiUsers size={11} color={T.textMuted} style={{ flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: "10px", fontWeight: 800, color: T.textMuted,
              textTransform: "uppercase", letterSpacing: "0.08em", lineHeight: 1,
            }}
          >
            {solo ? "Your assignment" : title}
          </Typography>
          {!solo && (
            <Typography
              sx={{
                ml: "auto", fontSize: "10.5px", fontWeight: 800,
                color: done === total ? "#15803D" : T.primaryDark, lineHeight: 1,
              }}
            >
              {done} of {total} done
            </Typography>
          )}
        </Box>

        {!solo && (
        <Box
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${done} of ${total} assignees completed`}
          sx={{ height: 4, borderRadius: 999, bgcolor: T.border, overflow: "hidden" }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{
              height: "100%",
              borderRadius: 999,
              background:
                done === total
                  ? "linear-gradient(90deg,#22C55E,#15803D)"
                  : `linear-gradient(90deg,${T.primary},${T.primaryDark})`,
            }}
          />
        </Box>
        )}
      </Box>

      <Box sx={{ height: "1px", bgcolor: T.borderLight }} />

      {/* Rows */}
      <Box
        sx={{
          maxHeight: 236, overflowY: "auto", p: "5px",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { background: T.border, borderRadius: 3 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
        }}
      >
        {assignees.map((a, i) => {
          const editable = canEdit(a);
          const displayName = solo ? "You" : a.name;
          const sub = [
            solo ? null : a.roleName,
            a.status === TASK_STATUS.COMPLETED && a.completedAt
              ? `done ${timeAgo(a.completedAt)}`
              : a.statusUpdatedAt
              ? `updated ${timeAgo(a.statusUpdatedAt)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <motion.div
              key={a.lineId || a._id || i}
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.16, delay: Math.min(i, 6) * 0.02, ease: "easeOut" }}
            >
              <Box
                sx={{
                  display: "flex", alignItems: "center", gap: "9px",
                  px: "9px", py: "6px", borderRadius: "8px",
                  transition: "background .13s",
                  "&:hover": editable ? { bgcolor: T.primaryLight } : {},
                }}
              >
                <Avatar
                  src={a.avatar || undefined}
                  sx={{
                    width: 26, height: 26, flexShrink: 0, fontSize: "10px", fontWeight: 700,
                    bgcolor: hueFor(a._id), color: "#fff", border: "2px solid #fff",
                    boxShadow: "0 1px 3px rgba(15,23,42,0.12)",
                  }}
                >
                  {initials(a.name)}
                </Avatar>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "12.5px", fontWeight: 600, color: T.text, lineHeight: 1.3,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {displayName}
                  </Typography>
                  {sub && (
                    <Typography
                      sx={{
                        fontSize: "10px", color: T.textMuted, lineHeight: 1.4,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                    >
                      {sub}
                    </Typography>
                  )}
                </Box>

                <StatusChip
                  status={a.status}
                  editable={editable}
                  busy={pendingId === a._id}
                  personName={displayName}
                  onMenuOpenChange={onMenuOpenChange}
                  onPick={(s) => onPickStatus(a._id, s)}
                />
              </Box>
            </motion.div>
          );
        })}
      </Box>
    </Box>
  );
};

export default AssigneeHoverCard;
